
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const ServerTOS = () => {
  const location = useLocation();
  const fromGeneralTOS = location.state?.from === 'general-tos' || document.referrer.includes('/general-tos');

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-8">
            {fromGeneralTOS ? (
              <Link to="/general-tos">
                <Button variant="ghost" className="text-nexx-green hover:text-green-700">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to General TOS
                </Button>
              </Link>
            ) : (
              <Link to="/vps-servers">
                <Button variant="ghost" className="text-nexx-green hover:text-green-700">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to VPS Servers
                </Button>
              </Link>
            )}
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center">
              <span className="mr-3">⚖️</span>
              Terms of Service: Bare Metal & Virtual Private Servers
            </h1>
            <div className="w-24 h-px bg-gray-300 mx-auto mt-6"></div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* Acceptable Use Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">1. Acceptable Use</h2>
              
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">No Illegal Activity</h3>
                  <p className="text-gray-700">You may not use our servers for any unlawful purposes, including but not limited to hosting or distributing malware, phishing schemes, or engaging in unauthorized access of systems.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">No Network Abuse</h3>
                  <p className="text-gray-700">Activities such as DDoS attacks, port scanning, or any form of network disruption are strictly prohibited.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">No Spam or Mass Mailing</h3>
                  <p className="text-gray-700">Sending unsolicited bulk messages or spam through our servers is not allowed.</p>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Resource Usage Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">2. Resource Usage</h2>
              
              <div className="space-y-4">
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Fair Usage Policy</h3>
                  <p className="text-gray-700">For VPS users, excessive consumption of CPU, memory, or disk I/O that adversely affects other clients may result in throttling or suspension.</p>
                </div>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Bare Metal Expectations</h3>
                  <p className="text-gray-700">While bare metal servers offer dedicated resources, clients are expected to manage workloads responsibly to prevent hardware degradation.</p>
                </div>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">No Unauthorized Resource Sharing</h3>
                  <p className="text-gray-700">Reselling or sharing server resources without prior authorization is prohibited.</p>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Security & Maintenance Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">3. Security & Maintenance</h2>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Client Responsibility</h3>
                  <p className="text-gray-700">Clients are responsible for maintaining the security of their servers, including regular updates and patch management.</p>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Monitoring & Enforcement</h3>
                  <p className="text-gray-700">We reserve the right to monitor server activity to ensure compliance with these terms.</p>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Incident Response</h3>
                  <p className="text-gray-700">In the event of a security breach or violation, we may suspend or terminate services without prior notice.</p>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Data & Backups Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">4. Data & Backups</h2>
              
              <div className="space-y-4">
                <div className="bg-green-50 border-l-4 border-green-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Data Integrity</h3>
                  <p className="text-gray-700">Clients are responsible for their data. We do not guarantee data integrity and recommend regular backups.</p>
                </div>
                
                <div className="bg-green-50 border-l-4 border-green-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Backup Services</h3>
                  <p className="text-gray-700">Backup solutions are available upon request and may incur additional charges.</p>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Termination & Suspension Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">5. Termination & Suspension</h2>
              
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Violation of Terms</h3>
                  <p className="text-gray-700">Engaging in prohibited activities may result in immediate suspension or termination of services.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">No Refund Policy</h3>
                  <p className="text-gray-700">Services terminated due to violations are not eligible for refunds.</p>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Footer Note */}
            <div className="mb-12 text-center">
              <p className="text-gray-600 mb-4">
                <strong>Note:</strong> This TOS is subject to change. Clients will be notified of significant amendments.
              </p>
              <p className="text-gray-600">
                For any questions or clarifications, please contact our support team.
              </p>
              <div className="mt-6">
                <Button className="bg-nexx-green hover:bg-green-700 text-white">
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServerTOS;
